import { useCallback, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Text } from '@opendoor/atoms/text';
import { Toasty } from '@opendoor/atoms/toast';
import { IconMail } from '@tabler/icons-react';
import { getShareableLink } from 'components/helpers/links';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { CopyLinkToast } from 'components/marketplace/shared/CopyLinkToast';

import { useObservability } from '../../../../helpers/observability';

function getShareableContent(slug: string, addressToken: string) {
  return `Check out this home I want to tour from Opendoor!\n\n${getShareableLink(
    slug,
    addressToken,
  )}`;
}

function getEmailLink(slug: string, addressToken: string) {
  return `mailto:?body=${encodeURIComponent(getShareableContent(slug, addressToken))}`;
}

const TOAST_DURATION_MS = 1_000;

type TourWithAgentProperty = Pick<MarketplaceProperty, 'addressToken' | 'slug'>;

const copyToClipboard = (text: string) => {
  if (navigator.clipboard) {
    void navigator.clipboard.writeText(text);
  }
};

export const TourWithAgentModal = ({
  property,
  isOpen,
  onClose,
}: {
  property: TourWithAgentProperty;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [isToastOpen, setIsToastOpen] = useState(false);
  const { trackJourneyEvent } = useObservability();

  const handleOnShare = useCallback(() => {
    if (property.addressToken) {
      trackJourneyEvent('marketplaceProperty', 'share', {
        addressUuid: property.addressToken,
        source: 'tour_with_agent_copy',
      });
    }
    copyToClipboard(getShareableLink(property.slug, property.addressToken));
    setIsToastOpen(true);
    onClose();
  }, [onClose, property.addressToken, property.slug, trackJourneyEvent]);

  const handleEmail = useCallback(() => {
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: property.addressToken,
      source: 'tour_with_agent_email',
    });
    window.open(getEmailLink(property.slug, property.addressToken), '_self');
  }, [property.addressToken, property.slug, trackJourneyEvent]);

  const closeToast = useCallback(() => {
    setIsToastOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) onClose();
    },
    [onClose],
  );

  return (
    <Toasty>
      <Dialog.Root role="dialog" open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog title="Tour with an agent">
          <Dialog.Description>
            Share this home with your agent. They need to get the access codes or lockbox info to
            tour.
          </Dialog.Description>
          <Col className="w-full md:w-150">
            <Row
              onClick={handleOnShare}
              analyticsName="tour-with-agent-copy"
              aria-label="Copy to clipboard"
              className="items-center justify-between border-b border-od-border-secondary py-6 transition-transform active:scale-[0.98]"
            >
              <Text as="span" className="text-body-sm">
                Copy link
              </Text>
              <ContentCopyIcon size={16} />
            </Row>
            <Row
              onClick={handleEmail}
              analyticsName="tour-with-agent-email"
              aria-label="Share in Email"
              className="items-center justify-between py-6 transition-transform active:scale-[0.98]"
            >
              <Text as="span" className="text-body-sm">
                Email
              </Text>
              <IconMail size={16} />
            </Row>
          </Col>
          <Row className="justify-end pt-6">
            <Button
              analyticsName="tour-with-agent-close"
              aria-label="Close"
              variant="primary"
              size="lg"
              onClick={onClose}
            >
              Close
            </Button>
          </Row>
        </Dialog>
      </Dialog.Root>
      <CopyLinkToast
        open={isToastOpen}
        message="Link copied to clipboard"
        onShown={closeToast}
        durationMs={TOAST_DURATION_MS}
      />
    </Toasty>
  );
};

function ContentCopyIcon({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <mask id="path-1-inside-1_3070_193338" fill="white">
        <rect x="0.5" y="3.5" width="12.5" height="16.25" rx="1.25" />
      </mask>
      <rect
        x="0.5"
        y="3.5"
        width="12.5"
        height="16.25"
        rx="1.25"
        stroke="currentColor"
        strokeWidth="3.75"
        mask="url(#path-1-inside-1_3070_193338)"
      />
      <path
        d="M4.25 1H14.25C14.9404 1 15.5 1.55964 15.5 2.25V16"
        stroke="currentColor"
        strokeWidth="1.875"
      />
    </svg>
  );
}
